<template>
    <div id="emergency-contact-information-form">
        <v-card>
            <v-card-title>
                <v-icon left :color="kingsGold">mdi-card-account-mail</v-icon>Parent/Guardian Contact Information Form
            </v-card-title>
            <v-card-subtitle>Please provide information for a parent, guardian or supportive person who could assist you
                during an emergency or difficult time.</v-card-subtitle>
            <v-alert v-if="alert" :color="dangerColor" class="mr-3 ml-3 mb-0" dense icon="mdi-alert-circle" text
                prominent>
                There are one or more required form fields that are empty. Please verify that all required form fields
                are filled in.
            </v-alert>
            <v-card-text>
                <v-form ref="parentGuardianEmergencyContactForm" v-model="valid" lazy-validation>
                    <v-container class="px-0" fluid>
                        <v-card-subtitle class="px-0">
                            <v-icon :color="kingsGold" left>mdi-account-box</v-icon>Basic Information
                        </v-card-subtitle>
                        <v-row>
                            <v-col cols="12" md="4">
                                <v-select v-model="applicant.parentGuardianEmergencyContact.title" :items="titleItems"
                                    label="Title" placeholder="Please select..." required :rules="titleRules">
                                </v-select>
                            </v-col>
                            <v-col cols="12" md="8">
                                <v-text-field v-model="applicant.parentGuardianEmergencyContact.fullName"
                                    label="Full Name" :rules="fullNameRules" required>
                                </v-text-field>
                            </v-col>
                            <v-col cols="12">
                                <v-text-field v-model="applicant.parentGuardianEmergencyContact.relationship"
                                    label="Relationship" :rules="relationshipRules" required>
                                </v-text-field>
                            </v-col>
                        </v-row>
                        <v-card-subtitle class="px-0">
                            <v-icon :color="kingsGold" left>mdi-home</v-icon> Address Information
                        </v-card-subtitle>
                        <v-row>
                            <v-col cols="12" class="py-0">
                                <v-checkbox v-model="sameAddress" hide-details label="Same address"
                                    @mousedown="setAddress()"></v-checkbox>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12" md="8">
                                <v-text-field v-model="applicant.parentGuardianEmergencyContact.address"
                                    label="Home Address" :rules="addressRules" required>
                                </v-text-field>
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-text-field v-model="applicant.parentGuardianEmergencyContact.postalCode"
                                    label="Postal Code" :rules="postalCodeRules" required>
                                </v-text-field>
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-text-field v-model="applicant.parentGuardianEmergencyContact.country" label="Country"
                                    :rules="countryRules" required>
                                </v-text-field>
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-text-field v-model="applicant.parentGuardianEmergencyContact.city" label="City"
                                    :rules="cityRules" required>
                                </v-text-field>
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-select v-model="applicant.parentGuardianEmergencyContact.province"
                                    :items="provinceItems" label="Province" placeholder="Please select..."
                                    :rules="provinceRules" required>
                                </v-select>
                            </v-col>
                            <v-col v-if="applicant.parentGuardianEmergencyContact.province == 'Other'" cols="12">
                                <v-text-field v-model="applicant.parentGuardianEmergencyContact.provinceOther"
                                    label="Province (Other)" :rules="provinceOtherRules" required>
                                </v-text-field>
                            </v-col>
                        </v-row>
                        <v-card-subtitle class="px-0">
                            <v-icon :color="kingsGold" left>mdi-card-account-details</v-icon>Contact Information
                        </v-card-subtitle>
                        <v-row>
                            <v-col cols="12" md="6">
                                <v-text-field v-model="applicant.parentGuardianEmergencyContact.homePhone"
                                    label="Home Phone" :rules="homePhoneRules" required>
                                </v-text-field>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-text-field v-model="applicant.parentGuardianEmergencyContact.cellPhone"
                                    label="Cell Phone" :rules="cellPhoneRules" required>
                                </v-text-field>
                            </v-col>
                            <v-col cols="12">
                                <v-text-field v-model="applicant.parentGuardianEmergencyContact.email" label="Email"
                                    :rules="emailRules" required>
                                </v-text-field>
                            </v-col>
                        </v-row>
                    </v-container>
                </v-form>
            </v-card-text>
            <v-card-actions>
                <v-spacer>
                </v-spacer>
                <v-btn class="white--text" :color="successColor" :disabled="!valid" @click="nextStep(3)">
                    Next
                </v-btn>
                <v-btn class="white--text" :color="kingsGrey" @click="prevStep(1)">
                    Previous
                </v-btn>
            </v-card-actions>
        </v-card>
    </div>
</template>

<script>
export default {
    name: 'emergency-contact-information-form',
    props: {
        applicant: {
            required: true
        }
    },
    data() {
        return {
            alert: false,
            addressRules: [
                v => !!v || 'Home address is required',
                v => (v && v.length >= 2) || 'Please provide a valid home address',
            ],
            cellPhoneRules: [
                v => !!v || 'Cell phone is required',
            ],
            cityRules: [
                v => !!v || 'City is required',
                v => (v && v.length >= 2) || 'Please provide a valid city',
            ],
            countryRules: [
                v => !!v || 'Country is required',
                v => (v && v.length >= 2) || 'Please provide a valid country',
            ],
            emailRules: [
                v => !!v || 'Email is required',
                v => (v && v.length >= 2) || 'Please provide a valid email',
            ],
            fullNameRules: [
                v => !!v || 'Full name is required',
                v => (v && v.length >= 2) || 'Full name must be more than 2 characters',
            ],
            homePhoneRules: [
                v => !!v || 'Home phone is required',
            ],
            relationshipRules: [
                v => !!v || 'Relationship is required',
                v => (v && v.length >= 2) || 'Please provide a valid relationship',
            ],
            postalCodeRules: [
                v => !!v || 'Postal code is required',
                v => (v && v.length >= 2) || 'Please provide a valid postal code',
            ],
            provinceItems: [{
                    text: 'Alberta',
                    value: 'AB'
                },
                {
                    text: 'British Columbia',
                    value: 'BC'
                },
                {
                    text: 'Manitoba',
                    value: 'MB'
                },
                {
                    text: 'New Brunswick',
                    value: 'NB'
                },
                {
                    text: 'Newfoundland and Labrador',
                    value: 'NL'
                },
                {
                    text: 'Northwest Territories',
                    value: 'NT'
                },
                {
                    text: 'Nova Scotia',
                    value: 'NS'
                },
                {
                    text: 'Nunavut',
                    value: 'NU'
                },
                {
                    text: 'Ontario',
                    value: 'ON'
                },
                {
                    text: 'Prince Edward Island',
                    value: 'PE'
                },
                {
                    text: 'Quebec',
                    value: 'QC'
                },
                {
                    text: 'Saskatchewan',
                    value: 'SK'
                },
                {
                    text: 'Yukon',
                    value: 'YT'
                },
                {
                    text: 'Other',
                    value: 'Other'
                }
            ],
            provinceRules: [
                v => !!v || 'Province is required'
            ],
            provinceOtherRules: [
                v => !!v || 'Province (other) is required',
                v => (v && v.length >= 2) || 'Please provide a valid province (other)',
            ],
            sameAddress: false,
            titleItems: [{
                    text: 'Mr.',
                    value: 'Mr'
                },
                {
                    text: 'Mrs.',
                    value: 'Mrs'
                },
                {
                    text: 'Ms.',
                    value: 'Ms'
                },
                {
                    text: 'Dr.',
                    value: 'Dr'
                }
            ],
            titleRules: [
                v => !!v || 'Title is required'
            ],
            valid: true
        }
    },
    methods: {
        nextStep(val) {
            if (this.$refs.parentGuardianEmergencyContactForm.validate()) {
                this.alert = false;
                this.$emit('setStep', val);
                this.$nextTick(() => {
                    this.$emit('scrollToTop');
                })
            } else {
                this.alert = true;
                this.$emit('scrollToTop');
            }
        },
        prevStep(val) {
            this.$emit('setStep', val);
            this.$nextTick(() => {
                this.$emit('scrollToTop');
            })
        },
        setAddress() {
            if (!this.sameAddress) {
                this.applicant.parentGuardianEmergencyContact.address = this.applicant.address;
                this.applicant.parentGuardianEmergencyContact.postalCode = this.applicant.postalCode;
                this.applicant.parentGuardianEmergencyContact.country = this.applicant.country;
                this.applicant.parentGuardianEmergencyContact.city = this.applicant.city;
                this.applicant.parentGuardianEmergencyContact.province = this.applicant.province;
                this.applicant.parentGuardianEmergencyContact.provinceOther = this.applicant.provinceOther;
            } else {
                this.applicant.parentGuardianEmergencyContact.address = '';
                this.applicant.parentGuardianEmergencyContact.postalCode = '';
                this.applicant.parentGuardianEmergencyContact.country = '';
                this.applicant.parentGuardianEmergencyContact.city = '';
                this.applicant.parentGuardianEmergencyContact.province = '';
                this.applicant.parentGuardianEmergencyContact.provinceOther = '';
            }
        }
    },
    computed: {
        dangerColor() {
            return '#f64e60';
        },
        kingsGold() {
            return '#d7ae28';
        },
        kingsGrey() {
            return '#6d6e71';
        },
        successColor() {
            return '#1bc5bd';
        }
    }
}
</script>

<style scoped>
.col-12 {
    padding-top: 0;
}

.v-input--checkbox {
    margin-top: 0;
}
</style>